import { Box, Divider, Flex, Group, Stack, Title } from '@mantine/core';
import { useMemo } from 'react';

import { CalculationResult, CalculationResultTotal, Declaration, EnvironmentCountries, TaxBase } from '@e-origin/shared';

import { useSettings } from '@hooks/useSettings';
import { renderField } from './DeclarationViewCustoms';
import classes from './DeclarationViewCustoms.module.css';

interface CalculationsDisplayValues {
  items: CalculationResult[];
  total: CalculationResultTotal[];
}

interface CalculationsDisplay {
  duties: CalculationsDisplayValues;
  vat: CalculationsDisplayValues;
}

interface DeclarationViewCalculationsProps {
  calculations: CalculationsDisplay;
  declaration: Declaration;
}

const getTaxBaseValue = (taxBase?: TaxBase) => {
  if (!taxBase) return undefined;

  if (taxBase.amount) {
    return `${taxBase.amount} €`;
  }

  if (taxBase.specificQuantity && taxBase.specificUnit) {
    return `${taxBase.specificQuantity} ${taxBase.specificUnit}`;
  }

  return ' ';
};

export const DeclarationViewCalculations: React.FC<DeclarationViewCalculationsProps> = ({ calculations, declaration }) => {
  const { data: settings } = useSettings();
  const isFR = settings?.country === EnvironmentCountries.FR;

  const showContent = useMemo(() => {
    return (
      !!declaration.customsState?.payment ||
      Object.keys(calculations).filter((type) => !!(calculations[type].items.length || calculations[type].total.length)).length
    );
  }, [calculations, declaration]);

  const calculationsLength = useMemo(() => {
    return Object.keys(calculations).length;
  }, [calculations]);

  return (
    <>
      {!!showContent && (
        <Box mb="xl">
          <Divider mb="xl" />
          {Object.keys(calculations).map(
            (type, typeIdx) =>
              !!(calculations[type].items.length || calculations[type].total.length) && (
                <Flex align="flex-start" key={type} mb={typeIdx < calculationsLength - 1 ? 'xl' : 0}>
                  <Box style={{ flexBasis: '50%' }}>
                    <Title order={3} className={classes.calculationItemsTitle}>
                      {type === 'duties' ? 'Duties' : 'VAT'}
                    </Title>
                    {calculations[type].items.map((item: CalculationResult, idx) => (
                      <Group key={idx} gap={30} align="flex-start">
                        {isFR && renderField('Item number', item.goodsItemNumber)}
                        {renderField('Tax Type', item.taxType)}
                        {isFR && renderField('National tax type', item.nationalTaxType)}
                        {isFR && renderField('Taxation status', item.taxationStatus)}
                        {isFR && renderField('Additional procedures', item.additionalProcedures?.join(', '))}
                        {item.taxBase && renderField('Tax base', getTaxBaseValue(item.taxBase))}
                        {item.taxBase && renderField('Rate', item.taxBase.taxRate, true)}
                        {renderField('Amount due', item.payableTaxAmount, true)}
                        {item.taxBase && renderField('Assessed amount', item.taxBase.taxAmount)}
                        {renderField('Payment method', item.paymentMethod)}
                      </Group>
                    ))}
                  </Box>

                  <Box className={classes.calculationBox}>
                    <Title order={3} className={classes.calculationItemsTitle}>
                      {type === 'duties' ? 'Total Duties' : 'Total VAT'}
                    </Title>
                    {calculations[type].total.map((item: CalculationResultTotal, idx) => (
                      <Group key={idx} gap={30} align="flex-start">
                        {renderField('Tax Type', item.taxType)}
                        {renderField('Total amount due', item.payableTaxAmount, item.payableTaxAmount === 0)}
                        {renderField('Total assessed amount', item.taxAmount, item.taxAmount === 0)}
                        {renderField('Payment method', item.paymentMethod)}
                      </Group>
                    ))}
                  </Box>
                </Flex>
              ),
          )}

          {declaration.customsState?.payment && (
            <Flex justify="flex-end">
              <Box className={classes.calculationBox}>
                <Title order={3} className={classes.calculationItemsTitle}>
                  Total overall
                </Title>
                <Stack>
                  <Group gap={30} align="flex-start">
                    {renderField('Total amount', declaration.customsState?.payment?.totalAmount, declaration.customsState?.payment?.totalAmount === 0)}
                    {renderField(
                      'Total assessed amount',
                      declaration.customsState?.payment?.totalAssessedAmount,
                      declaration.customsState?.payment?.totalAssessedAmount === 0,
                    )}
                  </Group>
                  {isFR && (
                    <Group gap={30} align="flex-start">
                      {renderField(
                        'Guaranteed amount',
                        declaration.customsState?.payment?.amountUsed?.guaranteedAmount,
                        declaration.customsState?.payment?.amountUsed?.guaranteedAmount === 0,
                      )}
                      {renderField(
                        'Unguaranteed amount',
                        declaration.customsState?.payment?.amountUsed?.unguaranteedAmount,
                        declaration.customsState?.payment?.amountUsed?.unguaranteedAmount === 0,
                      )}
                    </Group>
                  )}
                  {renderField('Bank reference', declaration.customsState?.payment?.bank?.reference)}
                  {renderField('Bank account', declaration.customsState?.payment?.bank?.account)}
                  {renderField('Payment reference number', declaration.customsState?.payment?.reference)}
                  {renderField('Due date', declaration.customsState?.payment?.dueDate?.toString())}
                </Stack>
              </Box>
            </Flex>
          )}
        </Box>
      )}
    </>
  );
};
